// src/App.js
import "./App.css";
import React, { useState, useEffect, useRef } from "react";
import {
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";

import CircuitBackground from "./components/CircuitBackground";
import CookieConsentBanner from "./components/CookieConsentBanner";
import MiniLogo from "./components/MiniLogo";
import LanguageSwitcher from "./components/LanguageSwitcher";
import MenuOverlay from "./components/MenuOverlay";
import usePageAnalytics from "./hooks/usePageAnalytics";
import { trackEvent } from "./lib/analytics";

import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import ServicesPage from "./pages/ServicesPage";
import CasesPage from "./pages/CasesPage";
import NexPage from "./pages/NexPage";
import ContactPage from "./pages/ContactPage";
import IntegrateAiPage from "./pages/IntegrateAiPage";
import AdvancedAnalyticsPage from "./pages/AdvancedAnalyticsPage";
import DataPlatformsPage from "./pages/DataPlatformsPage";

function Screen({ children, enabled = true }) {
  const prefersReducedMotion = useReducedMotion();

  const contentVariants = !enabled
    ? {
        initial: { opacity: 1, y: 0 },
        enter: { opacity: 1, y: 0, transition: { duration: 0 } },
        exit: { opacity: 1, y: 0, transition: { duration: 0 } },
      }
    : prefersReducedMotion
    ? {
        initial: { opacity: 0 },
        enter: { opacity: 1, transition: { duration: 0.22 } },
        exit: { opacity: 0, transition: { duration: 0.18 } },
      }
    : {
        initial: { opacity: 0, y: 20 },
        enter: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.48, ease: [0.22, 0.61, 0.36, 1] },
        },
        exit: {
          opacity: 0,
          y: 8,
          transition: { duration: 0.24, ease: [0.4, 0, 0.2, 1] },
        },
      };

  const veilVariants = !enabled
    ? {
        initial: { opacity: 0 },
        enter: { opacity: 0, transition: { duration: 0 } },
        exit: { opacity: 0, transition: { duration: 0 } },
      }
    : prefersReducedMotion
    ? {
        initial: { opacity: 0 },
        enter: { opacity: 0, transition: { duration: 0.18 } },
        exit: { opacity: 0.18, transition: { duration: 0.16 } },
      }
    : {
        initial: { opacity: 1, y: 0 },
        enter: {
          opacity: 0,
          y: "-4%",
          transition: { duration: 0.48, ease: [0.22, 0.61, 0.36, 1] },
        },
        exit: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.24, ease: [0.4, 0, 0.2, 1] },
        },
      };

  return (
    <main className="route-screen">
      <motion.div
        className="route-screen__veil"
        variants={veilVariants}
        initial="initial"
        animate="enter"
        exit="exit"
      />
      <motion.div
        className="route-screen__content"
        variants={contentVariants}
        initial="initial"
        animate="enter"
        exit="exit"
      >
        {children}
      </motion.div>
    </main>
  );
}

function ExternalRedirect({ to }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);

  return null;
}

function getScrollY() {
  return window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
}

function useScrollChromeVisibility(pathname) {
  const [visible, setVisible] = useState(true);
  const lastY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    if (pathname === "/") {
      setVisible(true);
      return undefined;
    }

    lastY.current = getScrollY();
    setVisible(true);

    const updateVisibility = (currentY) => {
      const delta = currentY - lastY.current;

      if (currentY <= 12) {
        setVisible(true);
        lastY.current = currentY;
        return;
      }

      if (Math.abs(delta) < 8) {
        return;
      }

      setVisible(delta < 0);
      lastY.current = currentY;
    };

    const update = () => {
      ticking.current = false;
      updateVisibility(getScrollY());
    };

    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;

      if (typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(update);
      } else {
        window.setTimeout(update, 16);
      }
    };

    const onLenisScroll = (event) => {
      updateVisibility(typeof event?.scroll === "number" ? event.scroll : getScrollY());
    };

    const unsubscribeLenis =
      typeof window.lenis?.on === "function"
        ? window.lenis.on("scroll", onLenisScroll)
        : undefined;

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (typeof unsubscribeLenis === "function") {
        unsubscribeLenis();
      } else if (typeof window.lenis?.off === "function") {
        window.lenis.off("scroll", onLenisScroll);
      }
      ticking.current = false;
    };
  }, [pathname]);

  return pathname === "/" ? true : visible;
}

function App() {
  const [uiReady, setUiReady] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const chromeVisible = useScrollChromeVisibility(location.pathname);
  usePageAnalytics(location, i18n.language);

  useEffect(() => {
    let cancelled = false;

    if (!document.fonts?.ready) {
      setFontsReady(true);
      return undefined;
    }

    document.fonts.ready.then(() => {
      if (!cancelled) setFontsReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (window.lenis?.scrollTo) window.lenis.scrollTo(0, { immediate: true });
    else window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname]);

  useEffect(() => {
    document.title = t("meta.title");
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", t("meta.description"));
    document.documentElement.lang = i18n.language;
  }, [i18n.language, t, location.pathname]);

  const chromeReady = location.pathname === "/" ? uiReady : fontsReady;

  return (
    <div className="App">
      <CircuitBackground uiReady={uiReady} />

      <AnimatePresence mode="wait">
        {location.pathname !== "/" && (
          <MiniLogo
            key="minilogo"
            concealed={!chromeVisible}
            onClick={() => {
              trackEvent("navigation_click", {
                path: location.pathname,
                target: "menu",
                label: "Menu",
                nav_type: "menu_open",
              });
              setMenuOpen(true);
            }}
          />
        )}
      </AnimatePresence>

      {chromeReady && location.pathname !== "/" && (
        <LanguageSwitcher concealed={!chromeVisible} />
      )}
      <MenuOverlay open={menuOpen} onClose={() => setMenuOpen(false)} />
      <CookieConsentBanner />

      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route
            path="/"
            element={
              <Screen enabled={fontsReady}>
                <HomePage
                  onNavigate={(to) => navigate(to)}
                  onLogoAnimationComplete={() => setUiReady(true)}
                  fontsReady={fontsReady}
                />
              </Screen>
            }
          />
          <Route
            path="/about"
            element={
              <Screen enabled={fontsReady}>
                <AboutPage />
              </Screen>
            }
          />
          <Route
            path="/services"
            element={
              <Screen enabled={fontsReady}>
                <ServicesPage />
              </Screen>
            }
          />
          <Route
            path="/services/integrate-ai"
            element={
              <Screen enabled={fontsReady}>
                <IntegrateAiPage />
              </Screen>
            }
          />
          <Route
            path="/services/advanced-analytics"
            element={
              <Screen enabled={fontsReady}>
                <AdvancedAnalyticsPage />
              </Screen>
            }
          />
          <Route
            path="/services/data-platforms"
            element={
              <Screen enabled={fontsReady}>
                <DataPlatformsPage />
              </Screen>
            }
          />
          <Route
            path="/nex"
            element={
              <Screen enabled={fontsReady}>
                <NexPage />
              </Screen>
            }
          />
          <Route
            path="/work"
            element={
              <Screen enabled={fontsReady}>
                <CasesPage />
              </Screen>
            }
          />
          <Route
            path="/work/:caseId"
            element={
              <Screen enabled={fontsReady}>
                <CasesPage />
              </Screen>
            }
          />
          <Route path="/cases" element={<Navigate to="/work" replace />} />
          <Route
            path="/contact"
            element={
              <Screen enabled={fontsReady}>
                <ContactPage />
              </Screen>
            }
          />
          <Route path="/gemeente-ai" element={<ExternalRedirect to="https://gemeenteai.nl/" />} />
          <Route path="/businesses" element={<Navigate to="/services" replace />} />
          <Route path="/installation-companies" element={<Navigate to="/work/installatieboutique" replace />} />
          <Route path="/gemeente" element={<ExternalRedirect to="https://gemeenteai.nl/" />} />
          <Route path="/mkb" element={<Navigate to="/services" replace />} />
          <Route
            path="/installatiebedrijven"
            element={<Navigate to="/work/installatieboutique" replace />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
    </div>
  );
}

export default App;
